<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import AgentsAPI from 'dashboard/api/agents';
import CanalProAPI from 'dashboard/api/canalpro';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import CanalProPageState from '../components/CanalProPageState.vue';
import {
  collectionFromResponse,
  errorMessage,
  formatDateTime,
  recordFromResponse,
} from '../utils/canalproData';

const router = useRouter();
const accounts = ref([]);
const agents = ref([]);
const loading = ref(true);
const error = ref('');
const peopleAccount = ref(null);
const settingsAccount = ref(null);
const selectedAssignments = ref([]);
const savingPeople = ref(false);
const savingSettings = ref(false);
const searchQuery = ref('');
const statusFilter = ref('');
const assignmentFilter = ref('');
const credentialFilter = ref('');
const syncingAccounts = ref(false);
const lastLiveSyncAt = ref(null);
let liveRefreshTimer;

const settingsForm = reactive({
  name: '',
  active: true,
  assignmentMode: 'individual',
  contact: {
    logoUrl: '',
    primaryEmail: '',
    leadEmails: '',
    primaryPhone: '',
    mobilePhone: '',
    state: '',
    city: '',
    street: '',
    streetNumber: '',
    zipCode: '',
  },
  publicationDefaults: { showPrice: true, displayAddress: true },
});

const selectedAccountName = computed(
  () =>
    peopleAccount.value?.name ||
    peopleAccount.value?.business_name ||
    'esta conta'
);
const accountUsers = account =>
  account?.users || account?.assigned_users || account?.assignedUsers || [];

const assignedUserIdsFor = account =>
  accountUsers(account)
    .map(user => Number(user.id || user.user_id || user))
    .filter(Boolean);

const credentialOptions = computed(() => {
  const options = new Map();
  accounts.value.forEach(account => {
    const credential = account.credential || {};
    if (credential.id) options.set(String(credential.id), credential);
  });
  return [...options.values()].sort((first, second) =>
    String(first.name || '').localeCompare(String(second.name || ''), 'pt-BR')
  );
});
const filteredAccounts = computed(() => {
  const term = searchQuery.value.trim().toLocaleLowerCase('pt-BR');

  return accounts.value.filter(account => {
    const matchesSearch =
      !term ||
      [
        account.name,
        account.business_name,
        account.contract_id,
        account.publisher_id,
        account.credential?.name,
      ]
        .filter(Boolean)
        .some(value => String(value).toLocaleLowerCase('pt-BR').includes(term));
    const matchesStatus =
      !statusFilter.value ||
      (statusFilter.value === 'active' && account.active !== false) ||
      (statusFilter.value === 'inactive' && account.active === false);
    const matchesAssignment =
      !assignmentFilter.value ||
      account.assignment_mode === assignmentFilter.value;
    const matchesCredential =
      !credentialFilter.value ||
      String(account.credential?.id) === String(credentialFilter.value);

    return (
      matchesSearch && matchesStatus && matchesAssignment && matchesCredential
    );
  });
});
const activeAccountsCount = computed(
  () => accounts.value.filter(account => account.active !== false).length
);
const activeListingsCount = computed(() =>
  accounts.value.reduce(
    (total, account) =>
      total +
      Number(account.active_listings_count || account.activeListingsCount || 0),
    0
  )
);
const assignedPeopleCount = computed(
  () =>
    new Set(accounts.value.flatMap(account => assignedUserIdsFor(account))).size
);
const hasFilters = computed(() =>
  Boolean(
    searchQuery.value ||
      statusFilter.value ||
      assignmentFilter.value ||
      credentialFilter.value
  )
);

const normalizeUsers = response => {
  const payload = response?.data?.payload || response?.data || [];
  return Array.isArray(payload) ? payload : payload.users || payload.data || [];
};

const assignmentFor = userId =>
  selectedAssignments.value.find(item => item.id === Number(userId));

const userIsAssigned = userId => Boolean(assignmentFor(userId));

const roleFor = userId => assignmentFor(userId)?.role || 'publisher';

const updateAccountInList = updated => {
  const index = accounts.value.findIndex(item => item.id === updated.id);
  if (index >= 0) accounts.value.splice(index, 1, updated);
};

const setAccounts = response => {
  accounts.value = collectionFromResponse(response);
  lastLiveSyncAt.value = new Date();
};

const refreshAccounts = async ({ silent = false } = {}) => {
  if (syncingAccounts.value) return;

  syncingAccounts.value = true;
  try {
    setAccounts(await CanalProAPI.syncAccounts());
  } catch (requestError) {
    if (!silent) {
      useAlert(
        errorMessage(
          requestError,
          'Não foi possível atualizar as contas pelo CanalPro.'
        )
      );
    }
  } finally {
    syncingAccounts.value = false;
  }
};

const loadAccounts = async () => {
  loading.value = true;
  error.value = '';
  try {
    const [accountsResponse, agentsResponse] = await Promise.all([
      CanalProAPI.syncAccounts(),
      AgentsAPI.get(),
    ]);
    setAccounts(accountsResponse);
    agents.value = normalizeUsers(agentsResponse);
  } catch (requestError) {
    error.value = errorMessage(
      requestError,
      'Não foi possível carregar as contas do CanalPro.'
    );
  } finally {
    loading.value = false;
  }
};

const openPeople = async account => {
  peopleAccount.value = account;
  selectedAssignments.value = accountUsers(account)
    .map(user => ({
      id: Number(user.id || user.user_id || user),
      role: user.role || 'publisher',
    }))
    .filter(assignment => assignment.id);

  try {
    const response = await CanalProAPI.accountUsers(account.id);
    const users = collectionFromResponse(response);
    if (users.length) {
      selectedAssignments.value = users
        .map(user => ({ id: Number(user.id), role: user.role || 'publisher' }))
        .filter(assignment => assignment.id);
    }
  } catch (requestError) {
    useAlert(
      errorMessage(
        requestError,
        'Não foi possível carregar as pessoas desta conta.'
      )
    );
  }
};

const toggleUser = userId => {
  const id = Number(userId);
  selectedAssignments.value = userIsAssigned(id)
    ? selectedAssignments.value.filter(assignment => assignment.id !== id)
    : [...selectedAssignments.value, { id, role: 'publisher' }];
};

const setUserRole = (userId, role) => {
  const id = Number(userId);
  selectedAssignments.value = selectedAssignments.value.map(assignment =>
    assignment.id === id ? { ...assignment, role } : assignment
  );
};

const savePeople = async () => {
  if (!peopleAccount.value) return;
  savingPeople.value = true;
  try {
    const response = await CanalProAPI.updateAccountUsers(
      peopleAccount.value.id,
      {
        users: selectedAssignments.value,
      }
    );
    updateAccountInList(recordFromResponse(response));
    peopleAccount.value = null;
    useAlert(
      'Pessoas atualizadas. Elas já podem cuidar dos anúncios desta conta.'
    );
  } catch (requestError) {
    useAlert(
      errorMessage(
        requestError,
        'Não foi possível salvar as pessoas desta conta.'
      )
    );
  } finally {
    savingPeople.value = false;
  }
};

const openSettings = account => {
  const settings = account.settings || {};
  const contact = settings.contact || {};
  const address = contact.address || {};
  const defaults =
    settings.publication_defaults || settings.publicationDefaults || {};
  settingsAccount.value = account;
  settingsForm.name = account.name || '';
  settingsForm.active = account.active !== false;
  settingsForm.assignmentMode = account.assignment_mode || 'individual';
  settingsForm.contact.logoUrl =
    contact.logo_url || contact.logoUrl || account.logo_url || '';
  settingsForm.contact.primaryEmail =
    contact.primary_email || contact.primaryEmail || '';
  settingsForm.contact.leadEmails = (
    contact.lead_emails ||
    contact.leadEmails ||
    []
  ).join('\n');
  settingsForm.contact.primaryPhone =
    contact.primary_phone || contact.primaryPhone || '';
  settingsForm.contact.mobilePhone =
    contact.mobile_phone || contact.mobilePhone || '';
  settingsForm.contact.state = address.state || '';
  settingsForm.contact.city = address.city || '';
  settingsForm.contact.street = address.street || '';
  settingsForm.contact.streetNumber =
    address.street_number || address.streetNumber || '';
  settingsForm.contact.zipCode = address.zip_code || address.zipCode || '';
  settingsForm.publicationDefaults.showPrice =
    defaults.show_price ?? defaults.showPrice ?? true;
  settingsForm.publicationDefaults.displayAddress =
    defaults.display_address ?? defaults.displayAddress ?? true;
};

const splitEmails = value =>
  String(value || '')
    .split(/[\n,;]+/)
    .map(email => email.trim())
    .filter(Boolean);

const saveSettings = async () => {
  if (!settingsAccount.value) return;
  savingSettings.value = true;
  try {
    const response = await CanalProAPI.updateAccount(settingsAccount.value.id, {
      name: settingsForm.name.trim(),
      active: settingsForm.active,
      assignment_mode: settingsForm.assignmentMode,
      contact: {
        logo_url: settingsForm.contact.logoUrl.trim(),
        primary_email: settingsForm.contact.primaryEmail.trim(),
        lead_emails: splitEmails(settingsForm.contact.leadEmails),
        primary_phone: settingsForm.contact.primaryPhone.trim(),
        mobile_phone: settingsForm.contact.mobilePhone.trim(),
        address: {
          state: settingsForm.contact.state.trim(),
          city: settingsForm.contact.city.trim(),
          street: settingsForm.contact.street.trim(),
          street_number: settingsForm.contact.streetNumber.trim(),
          zip_code: settingsForm.contact.zipCode.trim(),
        },
      },
      publication_defaults: {
        show_price: settingsForm.publicationDefaults.showPrice,
        display_address: settingsForm.publicationDefaults.displayAddress,
      },
    });
    updateAccountInList(recordFromResponse(response));
    settingsAccount.value = null;
    useAlert(
      'Configurações salvas. Os dados da empresa também foram atualizados no CanalPro.'
    );
  } catch (requestError) {
    useAlert(
      errorMessage(
        requestError,
        'Não foi possível salvar as configurações desta conta.'
      )
    );
  } finally {
    savingSettings.value = false;
  }
};

const clearFilters = () => {
  searchQuery.value = '';
  statusFilter.value = '';
  assignmentFilter.value = '';
  credentialFilter.value = '';
};

const openListings = account => {
  router.push({
    name: 'canalpro_listings_index',
    query: { account_id: account.id },
  });
};

onMounted(() => {
  loadAccounts();
  liveRefreshTimer = window.setInterval(
    () => refreshAccounts({ silent: true }),
    60000
  );
});

onBeforeUnmount(() => window.clearInterval(liveRefreshTimer));
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <main
    class="h-full min-h-0 overflow-y-auto overscroll-contain bg-slate-50 px-4 py-6 sm:px-6 lg:px-10"
  >
    <section class="mx-auto max-w-7xl">
      <header
        class="overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-950 via-slate-950 to-violet-950 px-5 py-6 text-white shadow-sm sm:px-7"
      >
        <div
          class="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between"
        >
          <div>
            <p class="text-sm font-medium text-indigo-200">
              CanalPro · Gandalf
            </p>
            <h1 class="mt-1 text-2xl font-semibold tracking-tight">
              Contas de publicação
            </h1>
            <p class="mt-2 max-w-3xl text-sm text-slate-300">
              Organize contratos, responsáveis e padrões de publicação de cada
              perfil da imobiliária.
            </p>
          </div>
          <button
            class="inline-flex items-center justify-center gap-2 rounded-xl bg-white/10 px-4 py-2.5 text-sm font-semibold text-white ring-1 ring-inset ring-white/20 transition hover:bg-white/15"
            type="button"
            :disabled="loading || syncingAccounts"
            @click="refreshAccounts"
          >
            <span
              class="i-lucide-refresh-cw"
              :class="syncingAccounts ? 'animate-spin' : ''"
            />
            {{ syncingAccounts ? 'Atualizando…' : 'Atualizar contas' }}
          </button>
        </div>
        <dl class="mt-6 grid grid-cols-3 gap-3 sm:max-w-2xl">
          <div class="rounded-xl bg-white/8 px-3 py-3">
            <dt class="text-xs text-slate-300">Contas ativas</dt>
            <dd class="mt-1 text-xl font-semibold">
              {{ activeAccountsCount }}
            </dd>
          </div>
          <div class="rounded-xl bg-white/8 px-3 py-3">
            <dt class="text-xs text-slate-300">Anúncios publicados</dt>
            <dd class="mt-1 text-xl font-semibold">
              {{ activeListingsCount }}
            </dd>
          </div>
          <div class="rounded-xl bg-white/8 px-3 py-3">
            <dt class="text-xs text-slate-300">Pessoas vinculadas</dt>
            <dd class="mt-1 text-xl font-semibold">
              {{ assignedPeopleCount }}
            </dd>
          </div>
        </dl>
        <p v-if="lastLiveSyncAt" class="mt-4 text-xs text-slate-300">
          Contas atualizadas pelo CanalPro às
          {{ formatDateTime(lastLiveSyncAt) }}. A atualização automática ocorre
          a cada minuto.
        </p>
      </header>

      <CanalProPageState
        v-if="loading || error || !accounts.length"
        class="mt-6"
        :loading="loading"
        :error="error"
        :empty="!loading && !error && !accounts.length"
        empty-title="Nenhuma conta encontrada"
        empty-description="Peça a um administrador para verificar um acesso do CanalPro e trazer as contas da imobiliária."
        @retry="loadAccounts"
      />

      <template v-else>
        <div
          class="mt-6 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5"
        >
          <div
            class="grid gap-4 lg:grid-cols-[minmax(16rem,1fr)_12rem_13rem_14rem_auto] lg:items-end"
          >
            <label class="grid gap-1.5 text-sm font-medium text-slate-700">
              Buscar conta
              <div class="relative">
                <span
                  class="i-lucide-search pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                /><input
                  v-model="searchQuery"
                  class="w-full rounded-lg border-slate-300 py-2 pl-9 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="Nome, contrato, publicador ou acesso"
                />
              </div>
            </label>
            <label class="grid gap-1.5 text-sm font-medium text-slate-700"
              >Status<select
                v-model="statusFilter"
                class="rounded-lg border-slate-300 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
              >
                <option value="">Todas</option>
                <option value="active">Ativas</option>
                <option value="inactive">Pausadas</option>
              </select></label
            >
            <label class="grid gap-1.5 text-sm font-medium text-slate-700"
              >Colaboração<select
                v-model="assignmentFilter"
                class="rounded-lg border-slate-300 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
              >
                <option value="">Todos os modos</option>
                <option value="individual">Individual</option>
                <option value="shared">Compartilhada</option>
              </select></label
            >
            <label class="grid gap-1.5 text-sm font-medium text-slate-700"
              >Credencial<select
                v-model="credentialFilter"
                class="rounded-lg border-slate-300 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
              >
                <option value="">Todos os acessos</option>
                <option
                  v-for="credential in credentialOptions"
                  :key="credential.id"
                  :value="credential.id"
                >
                  {{ credential.name }}
                </option>
              </select></label
            >
            <button
              class="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              type="button"
              :disabled="!hasFilters"
              @click="clearFilters"
            >
              Limpar
            </button>
          </div>
          <p class="mt-3 text-sm text-slate-500">
            {{ filteredAccounts.length }} de {{ accounts.length }} contas
          </p>
        </div>

        <CanalProPageState
          v-if="!filteredAccounts.length"
          class="mt-6"
          empty
          empty-title="Nenhuma conta corresponde aos filtros"
          empty-description="Ajuste os termos de busca ou limpe os filtros para visualizar outras contas."
        >
          <template #action>
            <button
              class="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              type="button"
              @click="clearFilters"
            >
              Limpar filtros
            </button>
          </template>
        </CanalProPageState>

        <div v-else class="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <article
            v-for="account in filteredAccounts"
            :key="account.id"
            class="flex min-h-80 flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
          >
            <div class="flex items-start gap-3">
              <img
                v-if="account.logo_url || account.logoUrl"
                :src="account.logo_url || account.logoUrl"
                class="h-12 w-12 rounded-full border border-slate-200 object-cover"
                :alt="account.name"
              />
              <span
                v-else
                class="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 font-semibold text-white"
              >
                {{
                  (account.name || account.business_name || 'C')
                    .slice(0, 1)
                    .toUpperCase()
                }}
              </span>
              <div class="min-w-0 flex-1">
                <h2 class="truncate font-semibold text-slate-900">
                  {{
                    account.name || account.business_name || 'Conta CanalPro'
                  }}
                </h2>
                <p class="mt-0.5 truncate text-sm text-slate-500">
                  {{
                    account.active === false
                      ? 'Conta pausada'
                      : 'Conta pronta para publicar'
                  }}
                </p>
              </div>
              <span
                class="shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold"
                :class="
                  account.active === false
                    ? 'bg-slate-100 text-slate-600'
                    : 'bg-emerald-50 text-emerald-700'
                "
                >{{ account.active === false ? 'Pausada' : 'Ativa' }}</span
              >
            </div>

            <div
              class="mt-4 rounded-xl bg-slate-50 px-3 py-2.5 text-xs text-slate-600"
            >
              <p class="truncate">
                <span class="font-semibold text-slate-700">Acesso:</span>
                {{ account.credential?.name || 'Não identificado' }}
              </p>
              <p class="mt-1 truncate">
                <span class="font-semibold text-slate-700">Contrato:</span>
                {{ account.contract_id || 'Não informado' }}
              </p>
            </div>

            <dl
              class="mt-6 grid grid-cols-2 gap-3 border-y border-slate-100 py-4 text-sm"
            >
              <div>
                <dt class="text-slate-500">Pessoas autorizadas</dt>
                <dd class="mt-1 font-semibold text-slate-900">
                  {{ assignedUserIdsFor(account).length }}
                </dd>
              </div>
              <div>
                <dt class="text-slate-500">Anúncios ativos</dt>
                <dd class="mt-1 font-semibold text-slate-900">
                  {{
                    account.active_listings_count ||
                    account.activeListingsCount ||
                    0
                  }}
                </dd>
              </div>
            </dl>

            <p class="mt-4 text-sm leading-6 text-slate-600">
              {{
                account.assignment_mode === 'shared'
                  ? 'As pessoas autorizadas podem colaborar nos mesmos anúncios.'
                  : 'Cada pessoa cuida dos anúncios que criou, enquanto gestores acompanham todos.'
              }}
            </p>
            <p class="mt-3 text-xs text-slate-500">
              <span
                class="i-lucide-cloud-check mr-1 inline-block align-text-bottom"
              />
              {{ formatDateTime(account.last_synced_at) }}
            </p>
            <div class="mt-auto grid gap-2 pt-5 sm:grid-cols-3">
              <button
                class="inline-flex items-center justify-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2.5 text-sm font-semibold text-indigo-700 transition hover:bg-indigo-100"
                type="button"
                @click="openListings(account)"
              >
                <span class="i-lucide-building-2 text-base" /> Anúncios
              </button>
              <button
                class="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 px-3 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                type="button"
                @click="openSettings(account)"
              >
                <span class="i-lucide-settings-2 text-base" /> Configurar
              </button>
              <button
                class="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 px-3 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-700"
                type="button"
                @click="openPeople(account)"
              >
                <span class="i-lucide-users-round text-base" /> Pessoas
              </button>
            </div>
          </article>
        </div>
      </template>
    </section>

    <LaisModal
      :show="Boolean(peopleAccount)"
      :title="`Quem pode cuidar de ${selectedAccountName}?`"
      max-width-class="max-w-2xl"
      @close="peopleAccount = null"
    >
      <p class="mb-5 text-sm text-slate-600">
        Escolha as pessoas e diga se elas cuidam apenas dos próprios anúncios ou
        de todos os anúncios desta conta.
      </p>
      <p
        v-if="!agents.length"
        class="rounded-lg bg-slate-50 p-4 text-sm text-slate-600"
      >
        Não há pessoas disponíveis nesta conta do sistema.
      </p>
      <ul
        v-else
        class="divide-y divide-slate-100 rounded-lg border border-slate-200"
      >
        <li
          v-for="agent in agents"
          :key="agent.id"
          class="grid gap-3 p-4 sm:grid-cols-[auto_1fr_12rem] sm:items-center"
        >
          <input
            :id="`canalpro-user-${agent.id}`"
            :checked="userIsAssigned(agent.id)"
            class="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
            type="checkbox"
            @change="toggleUser(agent.id)"
          />
          <label
            class="min-w-0 cursor-pointer"
            :for="`canalpro-user-${agent.id}`"
          >
            <span class="block truncate text-sm font-semibold text-slate-900">{{
              agent.name
            }}</span>
            <span class="block truncate text-sm text-slate-500">{{
              agent.email || 'Usuário do sistema'
            }}</span>
          </label>
          <select
            :value="roleFor(agent.id)"
            class="rounded-lg border-slate-300 text-sm focus:border-indigo-500 focus:ring-indigo-500 disabled:bg-slate-100"
            :disabled="!userIsAssigned(agent.id)"
            @change="setUserRole(agent.id, $event.target.value)"
          >
            <option value="publisher">Cuida dos próprios</option>
            <option value="manager">Acompanha todos</option>
          </select>
        </li>
      </ul>
      <template #footer>
        <div class="flex justify-end gap-3">
          <button
            class="rounded-lg px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
            type="button"
            @click="peopleAccount = null"
          >
            Cancelar
          </button>
          <button
            class="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
            type="button"
            :disabled="savingPeople"
            @click="savePeople"
          >
            {{ savingPeople ? 'Salvando…' : 'Salvar pessoas' }}
          </button>
        </div>
      </template>
    </LaisModal>

    <LaisModal
      :show="Boolean(settingsAccount)"
      title="Configurar conta"
      max-width-class="max-w-3xl"
      @close="settingsAccount = null"
    >
      <form class="grid gap-6" @submit.prevent="saveSettings">
        <section class="grid gap-4">
          <div>
            <h2 class="font-semibold text-slate-900">Dados da empresa</h2>
            <p class="mt-1 text-sm text-slate-500">
              Essas informações aparecem nos anúncios e são atualizadas no
              CanalPro.
            </p>
          </div>
          <div class="grid gap-4 sm:grid-cols-2">
            <label class="grid gap-1.5 text-sm font-medium text-slate-700">
              Nome para a equipe
              <input
                v-model="settingsForm.name"
                class="rounded-lg border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
                maxlength="120"
              />
            </label>
            <label class="grid gap-1.5 text-sm font-medium text-slate-700">
              Logo da empresa
              <input
                v-model="settingsForm.contact.logoUrl"
                class="rounded-lg border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
                type="url"
                placeholder="https://..."
              />
            </label>
            <label class="grid gap-1.5 text-sm font-medium text-slate-700">
              E-mail principal
              <input
                v-model="settingsForm.contact.primaryEmail"
                class="rounded-lg border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
                type="email"
              />
            </label>
            <label class="grid gap-1.5 text-sm font-medium text-slate-700">
              Telefone principal
              <input
                v-model="settingsForm.contact.primaryPhone"
                class="rounded-lg border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
                inputmode="tel"
              />
            </label>
            <label class="grid gap-1.5 text-sm font-medium text-slate-700">
              Celular
              <input
                v-model="settingsForm.contact.mobilePhone"
                class="rounded-lg border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
                inputmode="tel"
              />
            </label>
            <label class="grid gap-1.5 text-sm font-medium text-slate-700">
              CEP
              <input
                v-model="settingsForm.contact.zipCode"
                class="rounded-lg border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
                inputmode="numeric"
              />
            </label>
            <label class="grid gap-1.5 text-sm font-medium text-slate-700">
              Estado
              <input
                v-model="settingsForm.contact.state"
                class="rounded-lg border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
                maxlength="2"
              />
            </label>
            <label class="grid gap-1.5 text-sm font-medium text-slate-700">
              Cidade
              <input
                v-model="settingsForm.contact.city"
                class="rounded-lg border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
              />
            </label>
            <label
              class="grid gap-1.5 text-sm font-medium text-slate-700 sm:col-span-2"
            >
              Rua e número
              <span class="grid gap-3 sm:grid-cols-[1fr_8rem]">
                <input
                  v-model="settingsForm.contact.street"
                  class="rounded-lg border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
                />
                <input
                  v-model="settingsForm.contact.streetNumber"
                  class="rounded-lg border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="Número"
                />
              </span>
            </label>
            <label
              class="grid gap-1.5 text-sm font-medium text-slate-700 sm:col-span-2"
            >
              Outros e-mails de novos contatos
              <textarea
                v-model="settingsForm.contact.leadEmails"
                class="rounded-lg border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
                rows="2"
                placeholder="Um e-mail por linha"
              />
            </label>
          </div>
        </section>

        <section class="grid gap-3 border-t border-slate-200 pt-6">
          <h2 class="font-semibold text-slate-900">Como a equipe trabalha</h2>
          <label class="grid gap-1.5 text-sm font-medium text-slate-700">
            Organização dos anúncios
            <select
              v-model="settingsForm.assignmentMode"
              class="rounded-lg border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
            >
              <option value="individual">
                Cada pessoa cuida dos anúncios que criou
              </option>
              <option value="shared">
                Pessoas autorizadas podem colaborar nos mesmos anúncios
              </option>
            </select>
          </label>
          <label
            class="flex items-center justify-between gap-4 rounded-lg bg-slate-50 p-4 text-sm text-slate-700"
          >
            <span
              ><strong class="block text-slate-900"
                >Conta disponível para publicar</strong
              >Pause temporariamente sem apagar a configuração.</span
            >
            <input
              v-model="settingsForm.active"
              class="h-5 w-5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
              type="checkbox"
            />
          </label>
        </section>

        <section class="grid gap-3 border-t border-slate-200 pt-6">
          <h2 class="font-semibold text-slate-900">
            Padrão dos novos anúncios
          </h2>
          <label class="flex items-center gap-3 text-sm text-slate-700"
            ><input
              v-model="settingsForm.publicationDefaults.showPrice"
              class="h-4 w-4 rounded border-slate-300 text-indigo-600"
              type="checkbox"
            />
            Mostrar o valor do imóvel</label
          >
          <label class="flex items-center gap-3 text-sm text-slate-700"
            ><input
              v-model="settingsForm.publicationDefaults.displayAddress"
              class="h-4 w-4 rounded border-slate-300 text-indigo-600"
              type="checkbox"
            />
            Mostrar o endereço completo</label
          >
        </section>
      </form>
      <template #footer>
        <div class="flex justify-end gap-3">
          <button
            class="rounded-lg px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
            type="button"
            @click="settingsAccount = null"
          >
            Cancelar
          </button>
          <button
            class="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
            type="button"
            :disabled="savingSettings"
            @click="saveSettings"
          >
            {{ savingSettings ? 'Salvando…' : 'Salvar configurações' }}
          </button>
        </div>
      </template>
    </LaisModal>
  </main>
</template>
